import { ModalCloseButton, Box, useBreakpointValue } from "@chakra-ui/react";
import { LanguageSwitcher } from "@airhelp/react";
import { useTranslations } from "contexts/LocalizationContext/hooks";
import languages from "config/languages";
import { useModal } from "contexts/ModalContext";
import { useLanguage } from "contexts/LocalizationContext";
import { useCookies } from "react-cookie";
import { extractDomain, getExpDateByDays } from "utils/cookiesUtils";
import { trackUAEvent, trackCustomEvent } from "utils/tracking";
import { useRouter } from "next/router";
import { useCMSLanguageData, usePageName } from "contexts/DataContext/hooks";
import { getPageUrl } from "utils/getPageUrl";
import type { ListItem } from "@airhelp/react";
import buildLanguagesLists from "./utility";
import { LANGUAGE_COOKIE_EXPIRATION, LANGUAGE_COOKIE_NAME } from "./config";

const LANGUAGE_MODAL_SELECT_LANGUAGE_EVENT_UA = ["clicked", "LanguageModal"] as const;
const LANGUAGE_MODAL_SELECT_LANGUAGE_EVENT = { event: "cta_click", placement: "language_modal" } as const;

const LanguageSwitcherModal = () => {
  const { push } = useRouter();
  const translations = useTranslations();
  const { onClose } = useModal();
  const currentLanguage = useLanguage();
  const [, setCookie] = useCookies([LANGUAGE_COOKIE_NAME]);
  const cmsLanguagesData = useCMSLanguageData();
  const pageName = usePageName();

  const { selectLanguages, suggestedLanguages } = buildLanguagesLists(
    languages,
    pageName,
    cmsLanguagesData,
    getPageUrl
  );

  const createLanguageCookie = (language: string) => {
    const domain = extractDomain();
    setCookie(LANGUAGE_COOKIE_NAME, language, {
      domain,
      path: "/",
      expires: getExpDateByDays(LANGUAGE_COOKIE_EXPIRATION),
    });
  };

  const onItemClick = ({ id, href }: ListItem & { href: string }) => {
    createLanguageCookie(id);
    onClose();
    push(href);
    trackUAEvent([...LANGUAGE_MODAL_SELECT_LANGUAGE_EVENT_UA, id]);
    trackCustomEvent({ ...LANGUAGE_MODAL_SELECT_LANGUAGE_EVENT, label: id });
  };

  const content = [
    {
      listHeading: translations("suggested_languages"),
      listItems: suggestedLanguages,
    },
    {
      listHeading: translations("select_language"),
      listItems: selectLanguages,
      listAutoflow: { base: "row", lg: "column" },
    },
  ];

  return (
    <Box rounded={{ base: "none", lg: "md" }} margin={6} pt={12}>
      <ModalCloseButton
        position="absolute"
        top={2}
        right={2}
        color="primary.900"
        _hover={{ color: "primary.500", bg: "white" }}
        _active={{ color: "primary.700", bg: "white" }}
        size={useBreakpointValue({ base: "lg", lg: "md" })}
      />

      <LanguageSwitcher activeItems={[currentLanguage]} onItemClick={onItemClick} lists={content} />
    </Box>
  );
};

export default LanguageSwitcherModal;
